import { Time } from "@internationalized/date"
import { invalidateDocumentListData, type Role } from "@prismicio/editor-fields"
import { prettyPrintDate } from "@prismicio/editor-support/Date"
import { useStableCallback } from "@prismicio/editor-support/React"
import {
  AnimatedSuspense,
  Badge,
  Box,
  Button,
  DefaultErrorMessage,
  Dialog,
  DialogActionButton,
  DialogActions,
  DialogCancelButton,
  DialogContent,
  dialogErrorImage,
  DialogHeader,
  ErrorBoundary,
  Form,
  FormDateInput,
  FormInput,
  RadioCard,
  RadioGroup,
  Select,
  SelectItem,
  Skeleton,
  Text,
  Tooltip,
  useAlert,
} from "@prismicio/editor-ui"
import { useQueryClient } from "@tanstack/react-query"
import { type ReactNode, useState } from "react"

import { env } from "../../../app/env"
import {
  copyDocumentToRelease,
  createRelease as doCreateRelease,
  invalidateReleaseSizeData,
  invalidateReleasesListData,
  publishDocumentInRelease,
  type Release,
  updateReleasesListData,
} from "../../../app/services/release"
import { useRole } from "../../../app/services/user"
import { revalidateDocumentMeta } from "../../documentData"
import { useExistingDocument } from "../../DocumentProvider"
import {
  useGoToDocumentVersion,
  useRepositoryReleases,
} from "../../documentRouting"
import { actionAlert, type PendingActionProps } from "./HeaderButtons"

export function ReleaseButton(props: PendingActionProps) {
  const { pendingAction } = props

  const [mode, setMode] = useState<ReleaseMode>("create")
  const [release, setRelease] = useState<TargetRelease>()
  const [showDialog, setShowDialog] = useState(false)

  const { disabled, publishInRelease, isContributor } = useReleaseButton(props)
  const { valid, error } = validateForm(release)

  const button = (
    <Button
      startIcon="calendarToday"
      disabled={disabled}
      hiddenLabel="Release"
    />
  )

  function renderTooltip(trigger: ReactNode) {
    return (
      <Tooltip
        visible={isContributor}
        title={"You can't update documents"}
        content="Only users with the role of publisher or administrator can update documents."
      >
        {trigger}
      </Tooltip>
    )
  }

  async function submitForm() {
    if (!release) return
    if (!valid) return

    await publishInRelease(release)
    setShowDialog(false)
  }

  return (
    <Dialog
      trigger={button}
      size="small"
      renderTooltip={renderTooltip}
      open={showDialog}
      onOpenChange={setShowDialog}
      noDescription
    >
      <DialogHeader icon="calendarToday" title="Publishing document" />

      <DialogContent>
        <Box
          flexDirection="column"
          gap={16}
          padding={{ inline: 16, top: 16, bottom: 4 }}
        >
          <ModeSelect disabled={disabled} mode={mode} onModeChange={setMode} />
          <ReleasePicker
            mode={mode}
            release={release}
            onReleaseChange={setRelease}
            onSubmit={submitForm}
          />
        </Box>

        <DialogActions>
          <DialogCancelButton />
          <DialogActionButton
            loading={pendingAction === "release"}
            disabled={!valid}
            tooltipLabel={error}
            onClick={submitForm}
          >
            Add
          </DialogActionButton>
        </DialogActions>
      </DialogContent>
    </Dialog>
  )
}

function validateForm(release?: TargetRelease) {
  if (release === undefined) return { valid: false }

  if (release.type === "new" && release.label.length === 0)
    return { valid: false, error: "The release name is required" }

  if (release.type === "new" && release.date && release.date < new Date())
    return {
      valid: false,
      error: "The release date must be in the future",
    }

  return { valid: true }
}

const schedulePermissions: Record<Role, boolean> = {
  SuperUser: true,
  Owner: true,
  Administrator: true,
  Publisher: true,
  Writer: false,
  Contributor: false,
}

interface ModeSelectProps {
  disabled: boolean
  mode: ReleaseMode
  onModeChange: (mode: ReleaseMode) => void
}

function ModeSelect(props: ModeSelectProps) {
  const { mode, disabled, onModeChange } = props

  function modeTitle(mode: ReleaseMode) {
    switch (mode) {
      case "create":
        return "Create a new release"
      case "add":
        return "Add to existing release"
    }
  }

  return (
    <RadioGroup
      disabled={disabled}
      values={releaseModes}
      selectedValue={mode}
      onChange={onModeChange}
      evenItemSize
      renderItem={(mode) => <RadioCard title={modeTitle(mode)} />}
    />
  )
}

interface ReleasePickerProps {
  mode: ReleaseMode
  release?: TargetRelease
  onReleaseChange: (release: TargetRelease) => void
  onSubmit: () => void
}

function ReleasePicker(props: ReleasePickerProps) {
  const { mode, release, onReleaseChange, onSubmit } = props

  const pickerElement =
    mode === "create" ? (
      <CreateForm
        release={release?.type === "new" ? release : undefined}
        onReleaseChange={onReleaseChange}
        onSubmit={onSubmit}
      />
    ) : (
      <ReleaseSelect
        release={release?.type === "existing" ? release : undefined}
        onReleaseChange={onReleaseChange}
      />
    )

  return (
    <ErrorBoundary renderError={renderError}>
      <AnimatedSuspense key={mode} fallback={<SelectSkeleton />}>
        {pickerElement}
      </AnimatedSuspense>
    </ErrorBoundary>
  )
}

interface ReleaseSelectProps {
  release?: ExistingRelease
  onReleaseChange: (release: TargetRelease) => void
}

function ReleaseSelect(props: ReleaseSelectProps) {
  const { release, onReleaseChange } = props

  const { locale, data } = useExistingDocument()

  const releases = useRepositoryReleases().filter((release) => {
    if (data.version.status !== "release") return true
    return release.id !== data.version.release_id
  })

  const { role } = useRole(locale)

  const onValueChange = useStableCallback((value: string) => {
    const release = releases.find((release) => release.id === value)
    if (!release) return

    onReleaseChange({ type: "existing", releaseId: release.id })
  })

  if (releases.length === 0)
    return <Text variant="h3">There are no releases available</Text>

  const roleCanSchedule = schedulePermissions[role]

  return (
    <Box flexDirection="column" gap={4} flexGrow={1}>
      <Text variant="emphasized" component="label">
        Release name
      </Text>
      <Select
        flexContent
        placeholder="Please select a release"
        color="grey"
        constrainContentWidth
        value={release?.releaseId}
        onValueChange={onValueChange}
        sx={{ flexGrow: 1 }}
        sideOffset={4}
      >
        {releases.map((release) => (
          <ReleaseItem
            key={release.id}
            release={release}
            roleCanSchedule={roleCanSchedule}
          />
        ))}
      </Select>
      {!roleCanSchedule && (
        <Text variant="normal" color="grey11">
          Only users with the role of publisher or administrator can select
          scheduled releases.
        </Text>
      )}
    </Box>
  )
}

interface ReleaseItemProps {
  release: Release
  roleCanSchedule: boolean
}

function ReleaseItem(props: ReleaseItemProps) {
  const { release, roleCanSchedule } = props

  const releaseIsScheduled = Boolean(release.timestamp)

  const disabled = !roleCanSchedule && releaseIsScheduled

  return (
    <SelectItem key={release.id} value={release.id} disabled={disabled}>
      <Box justifyContent="space-between">
        <Text sx={{ width: 250 }} noWrap>
          {getReleaseDisplayName(release)}
        </Text>
        <Badge
          maxWidth={250}
          size="medium"
          title={getReleaseBadgeContent(release.timestamp)}
          icon="calendarToday"
          color={releaseIsScheduled ? "green" : "amber"}
        />
      </Box>
    </SelectItem>
  )
}

function getReleaseBadgeContent(timestamp?: Date) {
  if (timestamp === undefined) return "Not scheduled"

  return prettyPrintDate(timestamp)
}

interface CreateFormProps {
  release: NewRelease | undefined
  onReleaseChange: (release: NewRelease) => void
  onSubmit: () => void
}

function CreateForm(props: CreateFormProps) {
  const { release, onReleaseChange, onSubmit } = props

  function onNameChange(label: string) {
    onReleaseChange({ type: "new", label, date: release?.date })
  }

  function onTimeChanged(date: Date | undefined) {
    if (!date) return
    onReleaseChange({ type: "new", date, label: release?.label ?? "" })
  }

  const { locale } = useExistingDocument()

  const { role } = useRole(locale)

  const midnight = new Time(0)

  const disabled = !schedulePermissions[role]

  return (
    <Form sx={{ flexGrow: 1 }} onSubmit={onSubmit}>
      <FormInput
        value={release?.label ?? ""}
        label="Release name"
        onValueChange={onNameChange}
        placeholder="e.g: Website update"
      />
      <FormDateInput
        mode="date-time"
        label="Schedule publication"
        minValue="today"
        value={release?.date}
        onValueChange={onTimeChanged}
        defaultTime={midnight}
        disabled={disabled}
      />
      {disabled && (
        <Text variant="normal" color="grey11" sx={{ marginTop: 4 }}>
          Only users with the role of publisher or administrator can schedule
          documents.
        </Text>
      )}
    </Form>
  )
}

function SelectSkeleton() {
  return <Skeleton height={40} width="100%" />
}

function renderError() {
  return (
    <Box
      height="100%"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
    >
      <DefaultErrorMessage
        title="Oops!"
        description="An error occurred while fetching the releases."
        errorImageSrc={dialogErrorImage}
      />
    </Box>
  )
}

function useReleaseButton(props: PendingActionProps) {
  const { pendingAction, onPendingActionChange } = props
  const { data, edited, locale } = useExistingDocument()
  const goToDocumentVersion = useGoToDocumentVersion()

  const queryClient = useQueryClient()

  const { isContributor } = useRole(locale)

  const { meta, version } = data

  const { showAlert } = useAlert()

  const disabled =
    edited ||
    version.status === "archived" ||
    version.status === "published" ||
    Boolean(pendingAction) ||
    isContributor

  async function publishInRelease(release: TargetRelease) {
    if (disabled) return

    if (env.PRISMIC_DEMO) {
      showAlert({
        title: "Publishing is disabled for this demo.",
        subtitle: "This document would normally be added to the release.",
        color: "error",
        buttons: {
          action: {
            text: "Learn more",
            onClick: () => window.open("https://prismic.io/features/releases"),
          },
        },
      })
      return
    }

    onPendingActionChange("release")

    try {
      const targetReleaseId =
        release.type === "existing"
          ? release.releaseId
          : await createRelease(release)

      if (!targetReleaseId) {
        onPendingActionChange(undefined)
        return
      }

      // The current version is already in a release: Copy to the target release.
      if (version.status === "release") {
        await copyDocumentToRelease({
          documentId: meta.id,
          currentReleaseId: version.release_id,
          targetReleaseId,
        })
      }
      // This is a draft: add it to the target release.
      else {
        await publishDocumentInRelease(meta.id, targetReleaseId)
        void invalidateReleasesListData()

        // When publishing a document in a release, that particular release's size will be incremented
        void invalidateReleaseSizeData(targetReleaseId)
      }

      void invalidateDocumentListData(queryClient)

      await revalidateDocumentMeta(meta.id)

      goToDocumentVersion({ status: "release", releaseId: targetReleaseId })
    } catch (_) {
      actionAlert({
        action: "release",
        showAlert,
        onRetry: () => publishInRelease(release),
      })
    } finally {
      onPendingActionChange(undefined)
    }
  }

  return { disabled, publishInRelease, isContributor }
}

const releaseModes = ["create", "add"] as const
type ReleaseMode = (typeof releaseModes)[number]
type TargetRelease = NewRelease | ExistingRelease

interface NewRelease {
  type: "new"
  label: string
  date?: Date
}

interface ExistingRelease {
  type: "existing"
  releaseId: string
}

function getReleaseDisplayName(release: Release) {
  if (release.label) return release.label
  // Even though we now make it mandatory to provide a label for a scheduled release,
  // it wasn't the case in wroom so fallback to unknown.
  return "Unknown release"
}

function createRelease(release: NewRelease) {
  return doCreateRelease(release)
    .then((release) => {
      updateReleasesListData((releases) => [release, ...releases])
      return release.id
    })
    .catch(() => undefined)
}
